import { useMutation } from '@tanstack/react-query';
import { Copy, Download } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api, apiAt } from '@/lib/api';
import { REGIONS, getRegion, type Region } from '@/lib/regions';
import { Badge, Button, Dialog, ErrorNote } from './ui';

export type ImportOutcome = { kind: 'agent' | 'snippet'; slug: string; result: string; detail?: string };

const TONE: Record<string, 'ok' | 'warn' | 'bad' | 'neutral' | 'brand'> = {
  created: 'brand',
  published: 'ok',
  unchanged: 'neutral',
  draft: 'warn',
  skipped: 'warn',
  differs: 'warn',
  error: 'bad',
};

export const OutcomeList = ({ outcomes }: { outcomes: ImportOutcome[] }) => (
  <ul className="flex flex-col gap-1.5">
    {outcomes.map((o) => (
      <li key={`${o.kind}:${o.slug}`} className="flex flex-wrap items-center gap-2 text-[13px]">
        <Badge tone={TONE[o.result] ?? 'neutral'}>{o.result}</Badge>
        <span className="text-ink-3">{o.kind}</span>
        <span className="mono">{o.slug}</span>
        {o.detail && <span className="text-xs text-ink-2">— {o.detail}</span>}
      </li>
    ))}
  </ul>
);

const download = (name: string, data: unknown) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  a.click();
  URL.revokeObjectURL(url);
};

/** Export this agent as a bundle, or copy it straight into another region as a draft. */
export const PortabilityActions = ({ agentId, slug }: { agentId: string; slug: string }) => {
  const here = getRegion();
  const others = REGIONS.filter((r) => r.id !== here.id);
  const [target, setTarget] = useState<Region | null>(null);
  const [preview, setPreview] = useState<ImportOutcome[] | null>(null);

  const exportIt = useMutation({
    mutationFn: () => api(`/v1/agents/${agentId}/export`),
    onSuccess: (bundle) => download(`${slug}.agent.json`, bundle),
    onError: (e) => toast.error((e as Error).message),
  });
  const plan = useMutation({
    mutationFn: async (r: Region) => {
      const bundle = await api(`/v1/agents/${agentId}/export`);
      const res = await apiAt<{ outcomes: ImportOutcome[] }>(r, '/v1/import', { body: { bundle, dryRun: true } });
      return { bundle, outcomes: res.outcomes };
    },
    onSuccess: (res) => setPreview(res.outcomes),
  });
  const copy = useMutation({
    mutationFn: async () => apiAt<{ outcomes: ImportOutcome[] }>(target!, '/v1/import', { body: { bundle: plan.data!.bundle } }),
    onSuccess: (res) => {
      toast.success(`Copied to ${target!.label} — review and publish it there`);
      setPreview(res.outcomes);
      setTarget(null);
    },
  });

  return (
    <>
      <Button variant="ghost" loading={exportIt.isPending} onClick={() => exportIt.mutate()} title="Download the live version and its snippets as JSON">
        <Download className="size-4" /> Export
      </Button>
      {others.map((r) => (
        <Button
          key={r.id}
          variant="ghost"
          onClick={() => {
            setTarget(r);
            setPreview(null);
            plan.mutate(r);
          }}
        >
          <Copy className="size-4" /> Copy to {r.label}
        </Button>
      ))}
      <Dialog
        open={Boolean(target)}
        onClose={() => setTarget(null)}
        title={`Copy ${slug} to ${target?.label ?? ''}`}
        footer={
          <>
            <Button onClick={() => setTarget(null)}>Cancel</Button>
            <Button variant="primary" disabled={!preview || preview.some((o) => o.result === 'error')} loading={copy.isPending} onClick={() => copy.mutate()}>
              Copy as draft
            </Button>
          </>
        }
      >
        <p className="mb-3 text-[13px] text-ink-2">
          The live version (and any snippets it includes) goes to {target?.label} as a draft. Nothing there changes until someone publishes it.
        </p>
        {plan.isPending && <p className="text-[13px] text-ink-3">Checking {target?.label}…</p>}
        {plan.error ? <ErrorNote error={plan.error} /> : null}
        {preview && <OutcomeList outcomes={preview} />}
        {copy.error ? <ErrorNote error={copy.error} /> : null}
      </Dialog>
    </>
  );
};
